import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SHARED_ENV } from '../tokens';

export interface LanguageItem {
  label: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private env = inject(SHARED_ENV);

  private readonly STORAGE_KEY = 'lang';
  private readonly SUPPORTED_LANGS = ['en', 'hy', 'ru'];

  readonly languagesList: LanguageItem[] = [
    { label: 'English', value: 'en' },
    { label: 'Հայերեն', value: 'hy' },
    { label: 'Русский', value: 'ru' },
  ];

  get currentLang(): string {
    return this.translateService.getCurrentLang() || this.env.defaultLang;
  }

  init(): void {
    const urlLang = this.getLangFromUrl();
    const savedLang = localStorage.getItem(this.STORAGE_KEY);
    const lang = urlLang || savedLang || this.env.defaultLang;

    this.translateService.setFallbackLang(this.env.defaultLang);
    this.translateService.use(lang);
    localStorage.setItem(this.STORAGE_KEY, lang);
  }

  /** Called by langGuard to apply the language from the URL. */
  setLang(lang: string): void {
    if (this.SUPPORTED_LANGS.includes(lang)) {
      localStorage.setItem(this.STORAGE_KEY, lang);
      this.translateService.use(lang);
    }
  }

  /** Changes language and navigates to the same page with the new lang prefix. */
  onChange(lang: string): void {
    if (!this.SUPPORTED_LANGS.includes(lang)) return;

    localStorage.setItem(this.STORAGE_KEY, lang);
    this.translateService.use(lang);

    const url = this.router.url;
    const segments = url.split('/').filter(Boolean);

    if (segments.length > 0 && this.SUPPORTED_LANGS.includes(segments[0])) {
      segments[0] = lang;
    } else {
      segments.unshift(lang);
    }

    this.router.navigateByUrl('/' + segments.join('/'));
  }

  /** Returns a lang-prefixed absolute path. */
  langUrl(path: string): string {
    if (!path || path === '/') {
      return `/${this.currentLang}`;
    }
    return `/${this.currentLang}${path.startsWith('/') ? path : '/' + path}`;
  }

  private getLangFromUrl(): string | null {
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (segments.length > 0 && this.SUPPORTED_LANGS.includes(segments[0])) {
      return segments[0];
    }
    return null;
  }
}
